import {
  BookOpen,
  Compass,
  Ghost,
  Heart,
  Laugh,
  Library,
  type LucideIcon,
  Microscope,
  Rocket,
  Shield,
  Skull,
  Sparkles,
  Swords,
  Tag,
} from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardOverlayButton,
} from "@/components/ui/card";
import { focusOnPointerDown } from "@/lib/utils";
import AgeRatingBadge from "../../ds/icons/AgeRatingBadge";
import type { RegistrySeries } from "../../registry";
import { AudioBookPhase } from "../AudioBookPhase";
import { GenrePattern } from "./GenrePattern";

const genreIcons: Record<string, LucideIcon> = {
  adventure: Compass,
  fantasy: Sparkles,
  horror: Skull,
  mystery: Ghost,
  romance: Heart,
  comedy: Laugh,
  "sci-fi": Rocket,
  "science fiction": Rocket,
  action: Swords,
  thriller: Shield,
  science: Microscope,
};

function getGenreIcon(genreName?: string): LucideIcon {
  if (!genreName) return BookOpen;
  const key = genreName.toLowerCase();
  for (const [pattern, icon] of Object.entries(genreIcons)) {
    if (key.includes(pattern)) return icon;
  }
  return BookOpen;
}

interface SeriesCardProps {
  book: RegistrySeries;
  onClick: () => void;
  isSelected?: boolean;
}

export function SeriesCard({ book, onClick, isSelected }: SeriesCardProps) {
  const GenreIcon = getGenreIcon(book.genre?.name);
  // Was an imperative classList mutation in onError, which React clobbers on
  // the next render.
  const [coverFailed, setCoverFailed] = useState(false);
  const showCover = Boolean(book.cover) && !coverFailed;

  return (
    <Card className="overflow-hidden transition-colors hover:bg-accent/50 p-0 h-full">
      {/* One target for the whole card. First in the DOM so it comes first in
          the tab order; the heading below stays a real heading, which a button
          wrapping the card would have suppressed. */}
      <CardOverlayButton
        onClick={onClick}
        aria-label={book.title}
        aria-haspopup="dialog"
        aria-expanded={isSelected ?? false}
        onPointerDown={focusOnPointerDown}
      />
      {showCover && (
        <img
          src={book.cover}
          alt=""
          className="h-48 w-full object-cover"
          onError={() => setCoverFailed(true)}
        />
      )}
      {!showCover && (
        <div
          className="h-48 w-full bg-muted flex items-center justify-center relative overflow-hidden"
          aria-hidden="true"
        >
          <GenrePattern genre={book.genre?.name} />
          <GenreIcon className="h-12 w-12 text-muted-foreground/40 relative" />
        </div>
      )}
      <CardHeader className="relative">
        <h2 className="font-semibold text-sm leading-tight line-clamp-2">
          {book.title}
        </h2>
        {/* z-20 to stay above the card-wide overlay button, which would
            otherwise swallow its clicks and its tooltip. */}
        <div className="absolute right-1 -top-4 z-20">
          <AudioBookPhase phase={book.phase} />
        </div>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-1 relative">
        {book.genre && (
          <Badge variant="secondary" className="text-xs">
            <Library />
            {book.genre.name}
          </Badge>
        )}
        {book.secondaryGenres?.map((g) => (
          <Badge key={g.id} variant="default" className="text-xs">
            <Library />
            {g.name}
          </Badge>
        ))}
        {book.tags?.slice(0, 3).map((tag) => (
          <Badge key={tag} variant="outline" className="text-xs">
            <Tag />
            {tag}
          </Badge>
        ))}
        {(book.tags?.length ?? 0) > 3 && (
          <Badge variant="ghost" className="text-xs text-muted-foreground">
            and {book.tags!.length - 3}{" "}
            {book.tags!.length - 3 === 1 ? "other" : "others"}
          </Badge>
        )}
      </CardContent>
      <CardFooter className="px-4 pb-4 pt-0 flex items-center justify-between">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <BookOpen className="h-3.5 w-3.5" />
          <span>
            {book.episodeSet.length} episode
            {book.episodeSet.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {book.ageRestriction && (
            <AgeRatingBadge value={book.ageRestriction} />
          )}
        </div>
      </CardFooter>
    </Card>
  );
}
